import type { Node as ProseMirrorNode, NodeType, ResolvedPos } from 'prosemirror-model'
import type { Command, EditorState, Selection, Transaction } from 'prosemirror-state'
import { TextSelection } from 'prosemirror-state'
import { liftListItem, wrapInList } from 'prosemirror-schema-list'

interface SelectedBlock {
  node: ProseMirrorNode
  pos: number
}

function listNodeTypes(state: EditorState): NodeType[] {
  return [state.schema.nodes.bullet_list, state.schema.nodes.ordered_list].filter(Boolean)
}

/** The list type most closely wrapping the position, or null when the position is not in a list. */
function nearestListType($position: ResolvedPos, listTypes: readonly NodeType[]): NodeType | null {
  for (let depth = $position.depth; depth > 0; depth -= 1) {
    if (listTypes.includes($position.node(depth).type)) return $position.node(depth).type
  }
  return null
}

/** Whether every block the selection touches sits in a list of the given type. */
export function selectionCoveredByList(state: EditorState, listType: NodeType): boolean {
  const listTypes = listNodeTypes(state)
  const { from, to } = state.selection
  let sawTextblock = false
  let covered = true

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!covered) return false
    if (!node.isTextblock) return true

    sawTextblock = true
    if (nearestListType(state.doc.resolve(pos), listTypes) !== listType) covered = false
    return false
  })

  return sawTextblock && covered
}

/** The innermost list that wholly contains the selection, or null when the selection is not in one. */
function findEnclosingList(state: EditorState, listTypes: readonly NodeType[]): SelectedBlock | null {
  const { $from, $to } = state.selection

  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth)
    if (!listTypes.includes(node.type)) continue

    const pos = $from.before(depth)
    if ($to.pos > pos + node.nodeSize) return null

    return { node, pos }
  }

  return null
}

/** The outermost lists the selection reaches into, for selections that span whole blocks. */
function findSelectedLists(state: EditorState, listTypes: readonly NodeType[]): SelectedBlock[] {
  const { from, to } = state.selection
  const lists: SelectedBlock[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!listTypes.includes(node.type)) return true

    lists.push({ node, pos })
    return false
  })

  return lists
}

/** Blocks directly under the selection's shared parent that the selection touches. */
function selectedSiblingBlocks(state: EditorState): SelectedBlock[] {
  const { $from, $to } = state.selection
  const range = $from.blockRange($to)
  if (!range) return []

  const blocks: SelectedBlock[] = []
  let pos = range.start
  for (let index = range.startIndex; index < range.endIndex; index += 1) {
    const node = range.parent.child(index)
    blocks.push({ node, pos })
    pos += node.nodeSize
  }

  return blocks
}

/** The part of the selection that falls inside the given block. */
function clampSelectionToBlock(selection: Selection, block: SelectedBlock): { from: number; to: number } {
  return {
    from: Math.max(selection.from, block.pos + 1),
    to: Math.min(selection.to, block.pos + block.node.nodeSize - 1),
  }
}

/**
 * Toggles the list the selection sits inside; does nothing when the selection is not in one.
 * Removing lifts only the selected items out, while switching retypes the whole list.
 */
function toggleEnclosingList(listType: NodeType, itemType: NodeType): Command {
  return (state, dispatch) => {
    const enclosingList = findEnclosingList(state, listNodeTypes(state))
    if (!enclosingList) return false

    if (enclosingList.node.type === listType) return liftListItem(itemType)(state, dispatch)
    if (!listType.validContent(enclosingList.node.content)) return false

    if (dispatch) dispatch(state.tr.setNodeMarkup(enclosingList.pos, listType))
    return true
  }
}

/** Lifts the selected items out of every list the selection covers. */
function unwrapSelectedLists(listType: NodeType, itemType: NodeType): Command {
  return (state, dispatch) => {
    const lists = findSelectedLists(state, listNodeTypes(state))
    if (lists.length === 0) return false
    if (!dispatch) return true

    const combinedTransaction = state.tr
    let workingState = state

    for (const list of lists) {
      const clamped = clampSelectionToBlock(state.selection, list)
      const from = combinedTransaction.mapping.map(clamped.from)
      const to = combinedTransaction.mapping.map(clamped.to)
      const listSelection = TextSelection.between(workingState.doc.resolve(from), workingState.doc.resolve(to))
      workingState = workingState.apply(workingState.tr.setSelection(listSelection))

      toggleEnclosingList(listType, itemType)(workingState, (transaction) => {
        for (const step of transaction.steps) combinedTransaction.step(step)
        workingState = workingState.apply(transaction)
      })
    }

    dispatch(combinedTransaction.scrollIntoView())
    return true
  }
}

/** Retypes the wrong-type lists the selection reaches into, leaving their surroundings as they are. */
function retypeNestedLists(listType: NodeType): Command {
  return (state, dispatch) => {
    const listTypes = listNodeTypes(state)
    const { from, to } = state.selection
    const wrongTypeLists: SelectedBlock[] = []

    state.doc.nodesBetween(from, to, (node, pos) => {
      if (!listTypes.includes(node.type) || node.type === listType) return true

      wrongTypeLists.push({ node, pos })
      return false
    })

    const convertibleLists = wrongTypeLists.filter((list) => listType.validContent(list.node.content))
    if (convertibleLists.length === 0) return false

    if (dispatch) {
      const transaction = state.tr
      for (const list of convertibleLists) transaction.setNodeMarkup(list.pos, listType)
      dispatch(transaction.scrollIntoView())
    }

    return true
  }
}

/** Puts every block the selection touches into a list of the given type. */
function applyListToSelection(listType: NodeType, itemType: NodeType): Command {
  return (state, dispatch) => {
    const listTypes = listNodeTypes(state)
    const combinedTransaction = state.tr
    let workingState = state
    const collectSteps = (transaction: Transaction) => {
      for (const step of transaction.steps) combinedTransaction.step(step)
      workingState = workingState.apply(transaction)
    }

    for (const block of selectedSiblingBlocks(state)) {
      if (block.node.type === listType) continue
      const isOtherListType = listTypes.includes(block.node.type)
      if (!isOtherListType && !block.node.isTextblock) continue

      const clamped = clampSelectionToBlock(state.selection, block)
      const from = combinedTransaction.mapping.map(clamped.from)
      const to = combinedTransaction.mapping.map(clamped.to)
      const blockSelection = TextSelection.between(workingState.doc.resolve(from), workingState.doc.resolve(to))
      workingState = workingState.apply(workingState.tr.setSelection(blockSelection))

      // wrapInList fails for blocks a list item cannot hold (e.g. headings), leaving them out of the list.
      const applyToBlock = isOtherListType ? toggleEnclosingList(listType, itemType) : wrapInList(listType)
      applyToBlock(workingState, collectSteps)
    }

    if (combinedTransaction.steps.length === 0) return false
    if (dispatch) dispatch(combinedTransaction.scrollIntoView())
    return true
  }
}

/**
 * Joins adjacent sibling lists of the given type around the edited range, so numbering runs
 * through them as one list. Adjacent lists elsewhere in the document are left alone.
 */
function joinAdjacentLists(transaction: Transaction, listType: NodeType, selection: Selection) {
  let joined = true
  while (joined) {
    joined = false
    const editedFrom = transaction.mapping.map(selection.from)
    const editedTo = transaction.mapping.map(selection.to)

    const $edited = transaction.doc.resolve(editedFrom)
    let innermostListPos = -1
    for (let depth = $edited.depth; depth > 0; depth -= 1) {
      if ($edited.node(depth).type !== listType) continue
      innermostListPos = $edited.before(depth)
      break
    }

    transaction.doc.descendants((node, pos) => {
      if (joined) return false
      if (node.type !== listType) return true

      const boundary = pos + node.nodeSize
      const nextNode = transaction.doc.resolve(boundary).nodeAfter
      if (nextNode?.type !== listType) return true
      if (pos > editedTo || boundary + nextNode.nodeSize < editedFrom) return true

      // A list that merely contains the edited range was not itself edited; only the innermost
      // list holding the edit merges with its neighbors.
      const nodeContainsEdit = pos <= editedFrom && boundary >= editedTo
      if (nodeContainsEdit && pos !== innermostListPos) return true
      const nextContainsEdit = boundary <= editedFrom && boundary + nextNode.nodeSize >= editedTo
      if (nextContainsEdit && boundary !== innermostListPos) return true

      transaction.join(boundary)
      joined = true
      return false
    })
  }
}

/**
 * Applies a list type to the selection the way a word processor does: unwrapping when everything
 * selected is already in that list type, and otherwise putting every selected block into the list,
 * merged with equal lists directly around it.
 */
export function toggleList(listType: NodeType, itemType: NodeType): Command {
  return (state, dispatch) => {
    const listTypes = listNodeTypes(state)

    if (selectionCoveredByList(state, listType)) {
      if (findEnclosingList(state, listTypes)) return liftListItem(itemType)(state, dispatch)
      return unwrapSelectedLists(listType, itemType)(state, dispatch)
    }

    const joinAndDispatch = dispatch
      ? (transaction: Transaction) => {
          joinAdjacentLists(transaction, listType, state.selection)
          dispatch(transaction)
        }
      : undefined

    const enclosingList = findEnclosingList(state, listTypes)
    if (enclosingList) {
      // A matching enclosing list with uncovered blocks inside means the other type lives in
      // nested sublists: those get the new type, not the matching ancestor.
      if (enclosingList.node.type === listType) return retypeNestedLists(listType)(state, joinAndDispatch)
      return toggleEnclosingList(listType, itemType)(state, joinAndDispatch)
    }
    if (findSelectedLists(state, listTypes).length === 0) return wrapInList(listType)(state, joinAndDispatch)
    return applyListToSelection(listType, itemType)(state, joinAndDispatch)
  }
}
